import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { getBrowserPlatform } from 'helpers/browser';

import { PUBLIC_CORE_APP_URL_ENV } from 'tools/environments';

import { Icon } from 'core/generic/elements/icons';

import styles from './mobile-app-install-banner.module.scss';

type MobileAppInstallBannerProps = {
	appName?: string;
	publisherName?: string;
	logoSrc?: string;
	logo?: ReactNode;
	showOnPaths?: string[];
	androidStoreUrl?: string;
	iosStoreUrl?: string;
	dismissForDays?: number;
	closeLabel?: string;
	installLabel?: string;
	className?: string;
	onInstall?: () => void;
};

const LOCAL_STORAGE_KEY = 'mancity-mobile-app-banner-hide-until';
const DEFAULT_DISMISS_PERIOD_MS = 90 * 24 * 60 * 60 * 1000;

const isStandalone = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	const navigatorWithStandalone = navigator as Navigator & {
		standalone?: boolean;
	};

	return (
		Boolean(navigatorWithStandalone.standalone) ||
		(window.matchMedia &&
			window.matchMedia('(display-mode: standalone)').matches)
	);
};

const getStorageExpiry = () => {
	if (typeof window === 'undefined') {
		return 0;
	}

	try {
		const value = Number(
			window.localStorage.getItem(LOCAL_STORAGE_KEY) ?? 0
		);
		return Number.isFinite(value) ? value : 0;
	} catch {
		return 0;
	}
};

const setStorageExpiry = (expiresAt: number) => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		window.localStorage.setItem(LOCAL_STORAGE_KEY, String(expiresAt));
	} catch {
		// Ignore write errors
	}
};

const normalizePath = (path: string): string => {
	if (!path) {
		return '/';
	}

	return path === '/' ? '/' : path.replace(/\/+$/, '');
};

export function MobileAppInstallBanner({
	appName = 'Manchester City Official App',
	publisherName = 'Manchester City FC Ltd',
	logoSrc,
	logo,
	showOnPaths,
	androidStoreUrl = 'https://play.google.com/store/apps/details?id=com.mancity&hl=en_GB',
	iosStoreUrl = 'https://itunes.apple.com/gb/app/cityapp-man-city-official-app/id416127886',
	dismissForDays = 90,
	closeLabel = 'Close install banner',
	installLabel = 'View',
	className,
	onInstall,
}: MobileAppInstallBannerProps) {
	const [visible, setVisible] = useState(false);

	const platform = useMemo(() => getBrowserPlatform(), []);
	const isAvailableForInstall = platform === 'android' || platform === 'ios';
	const isAllowedPath = useMemo(() => {
		if (!showOnPaths || showOnPaths.length === 0) {
			return true;
		}

		if (typeof window === 'undefined') {
			return false;
		}

		const currentPath = normalizePath(window.location.pathname);
		return showOnPaths
			.map((path) => normalizePath(path))
			.includes(currentPath);
	}, [showOnPaths]);

	const shouldHide =
		typeof window === 'undefined' || getStorageExpiry() > Date.now();

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		if (
			!isAllowedPath ||
			!isAvailableForInstall ||
			isStandalone() ||
			shouldHide
		) {
			setVisible(false);
			return;
		}

		setVisible(true);
	}, [isAllowedPath, isAvailableForInstall, platform, shouldHide]);

	const handleClose = () => {
		const expiresAt =
			Date.now() +
			(dismissForDays * 24 * 60 * 60 * 1000 || DEFAULT_DISMISS_PERIOD_MS);
		setStorageExpiry(expiresAt);
		setVisible(false);
	};

	const handleInstall = () => {
		const storeUrl = platform === 'ios' ? iosStoreUrl : androidStoreUrl;
		if (storeUrl) {
			window.location.assign(storeUrl);
		}
		onInstall?.();
		setVisible(false);
	};

	if (!visible || !isAvailableForInstall) {
		return null;
	}

	const logoContent =
		logo ??
		(logoSrc ? (
			<img
				className={styles.logoImage}
				src={logoSrc}
				alt={`${appName} logo`}
				width={53}
				height={53}
			/>
		) : (
			<img
				alt="Manchester City FC"
				width="67"
				height="67"
				src={`${PUBLIC_CORE_APP_URL_ENV}/assets/images/crest/ManCity-Coloured.svg`}
			/>
		));

	return (
		<aside
			className={[styles.banner, className].filter(Boolean).join(' ')}
			role="status"
			aria-live="polite"
			data-theme="dark"
		>
			<div className={styles.inner}>
				<button
					type="button"
					className={styles.closeButton}
					onClick={handleClose}
					aria-label={closeLabel}
				>
					<Icon variant="Close" className={styles.closeButtonIcon} />
				</button>

				<div className={styles.logo}>{logoContent}</div>

				<div className={styles.description}>
					<span className={styles.appName}>{appName}</span>
					<span className={styles.appPublisher}>{publisherName}</span>
				</div>

				<button
					type="button"
					className={styles.installButton}
					onClick={handleInstall}
				>
					{installLabel}
				</button>
			</div>
		</aside>
	);
}

export default MobileAppInstallBanner;
